import { bal, natural } from "./ledger.js";
import { ACCOUNTS, COS } from "./reference.js";
import { P, peso } from "./format.js";
import { S } from "../app/store.js";
import { runTot } from "./payroll.js";
import { addDays, roll } from "./dates.js";

function taxFigures() {
  const q3e = "2026-09-30";
  const outVat = -bal("2240", q3e),
    inVat = bal("1410", q3e),
    vatDue = outVat - inVat;
  const ewtQ3 = -bal("2230", q3e);
  const rev = ACCOUNTS.filter((a) => a[2] === "R").reduce(
      (x, a) => x + natural(a[0], q3e),
      0,
    ),
    cogs = COS.reduce((x, c) => x + natural(c, q3e, "2026-01-01"), 0);
  let opex = 0;
  for (const a of ACCOUNTS)
    if (a[2] === "X" && !COS.includes(a[0]))
      opex += natural(a[0], q3e, "2026-01-01");
  const nti = rev - cogs - opex,
    projected = Math.round((nti * 12) / 9),
    assets = ACCOUNTS.filter((a) => a[2] === "A").reduce(
      (x, a) => x + bal(a[0], q3e),
      0,
    );
  const small = projected <= P(5000000) && assets <= P(100000000),
    rate = small ? 0.2 : 0.25;
  const rcit = Math.round(nti * rate),
    mcit = Math.round((rev - cogs) * 0.02),
    due = Math.max(rcit, mcit),
    cwt = bal("1420", q3e),
    prior = bal("1430", q3e);
  const oct = (t) =>
    S.runs
      .filter((r) => r.pay.slice(0, 7) === "2026-10" && r.status !== "DRAFT")
      .reduce((a, r) => a + runTot(r)[t], 0);
  return {
    outVat,
    inVat,
    vatDue,
    ewtQ3,
    rev,
    cogs,
    opex,
    nti,
    projected,
    assets,
    small,
    rate,
    rcit,
    mcit,
    itDue: due - cwt - prior,
    cwt,
    prior,
    wtcOct: oct("tax"),
    sssOct: oct("sssEE") + oct("sssER") + oct("ec"),
    phOct: oct("phEE") + oct("phER"),
    hdOct: oct("hdEE") + oct("hdER"),
  };
}

function birCalendar() {
  const f = taxFigures();
  return [
    {
      k: "1601-C|2026-09",
      form: "1601-C",
      per: "September 2026",
      due: roll("2026-10-10"),
      amt: null,
      basis: "NIRC §58(A), RR 11-2018 — withholding on compensation",
    },
    {
      k: "2550Q|2026-Q3",
      form: "2550Q",
      per: "Q3 2026",
      due: roll("2026-10-25"),
      amt: f.vatDue,
      basis: "NIRC §114(A) as amended by RA 10963; quarterly VAT",
    },
    {
      k: "2307|2026-Q3",
      form: "2307 to payees",
      per: "Q3 2026",
      due: roll("2026-10-20"),
      amt: null,
      basis:
        "RR 2-98 §2.58(B): certificates to subconsultants and lessor within 20 days after the quarter",
    },
    {
      k: "1601-EQ|2026-Q3",
      form: "1601-EQ",
      per: "Q3 2026",
      due: roll("2026-10-31"),
      amt: f.ewtQ3,
      basis:
        "RR 2-98 as amended: subconsultants 10%/15%, rent 5%; QAP attached",
    },
    {
      k: "1601-C|2026-10",
      form: "1601-C",
      per: "October 2026",
      due: roll("2026-11-10"),
      amt: f.wtcOct,
      basis: "NIRC §58(A); amount grows as October runs are approved",
    },
    {
      k: "SSS|2026-10",
      form: "SSS R-5 / PRN",
      per: "October 2026",
      due: roll("2026-11-30"),
      amt: f.sssOct,
      basis: "RA 11199; due by end of the following month",
    },
    {
      k: "PHIC|2026-10",
      form: "PhilHealth RF-1",
      per: "October 2026",
      due: roll("2026-11-15"),
      amt: f.phOct,
      basis: "RA 11223; schedule by employer number",
    },
    {
      k: "HDMF|2026-10",
      form: "Pag-IBIG MCRF",
      per: "October 2026",
      due: roll("2026-11-15"),
      amt: f.hdOct,
      basis: "RA 9679; schedule by employer number",
    },
    {
      k: "1702Q|2026-Q3",
      form: "1702Q + SAWT",
      per: "Q3 2026",
      due: roll(addDays("2026-09-30", 60)),
      amt: Math.max(0, f.itDue),
      basis: `NIRC §27(A) as amended by RA 11534; ${f.rate * 100}% RCIT${f.itDue < 0 ? `; 2307s exceed tax due by ${peso(-f.itDue)}, nothing to pay` : ""}`,
    },
    {
      k: "13M|2026",
      form: "13th month pay",
      per: "2026",
      due: "2026-12-24",
      amt: null,
      basis: "PD 851; pay on or before 24 Dec; tax-exempt up to ₱90,000",
    },
    {
      k: "1604-C|2026",
      form: "1604-C + 2316",
      per: "CY 2026",
      due: roll("2027-01-31"),
      amt: null,
      basis: "RR 11-2018 §2.83; Form 2316 to employees by 31 Jan",
    },
    {
      k: "MAYOR|2027",
      form: "Business permit renewal",
      per: "2027",
      due: roll("2027-01-20"),
      amt: null,
      basis:
        "Local Government Code §165; city treasurer, on 2026 gross receipts",
    },
    {
      k: "1604-E|2026",
      form: "1604-E",
      per: "CY 2026",
      due: roll("2027-03-01"),
      amt: null,
      basis:
        "RR 2-98 §2.58(C): annual EWT information return with alphalist of payees",
    },
    {
      k: "1702RT|2026",
      form: "1702-RT + AFS",
      per: "CY 2026",
      due: roll("2027-04-15"),
      amt: null,
      basis:
        "NIRC §77; audited FS by an accredited CPA (§232) when quarterly sales exceed ₱150,000; carry-over or refund election is irrevocable (§76)",
    },
    {
      k: "GIS|2027",
      form: "SEC GIS",
      per: "2027",
      due: null,
      amt: null,
      basis:
        "Revised Corporation Code §177: within 30 days after the annual stockholders' meeting",
    },
  ].map((x) => ({ ...x, filed: S.filed[x.k] }));
}

export { taxFigures, birCalendar };
